import React, { useState } from 'react';
import { Milestone } from '../types';
import { updateMilestone } from '../firebase/milestones.service';
import { ChatIcon, DocumentIcon, EditIcon } from '../../../icons';

interface MilestoneCardProps {
  milestone: Milestone;
  onUpdate?: (updatedMilestone: Milestone) => void;
}

/**
 * MilestoneCard - Weergave van een milestone met vraag en bewerkbaar antwoord
 * 
 * VISUELE HIËRARCHIE:
 * 1. Titel — duidelijk maar niet zwaar
 * 2. Vraag — de sparring vraag die leidde tot het antwoord (indien beschikbaar)
 * 3. Antwoord — het antwoord van de gebruiker, bewerkbaar
 * 4. Global period — context, zeer subtiel
 */
const MilestoneCard: React.FC<MilestoneCardProps> = ({ milestone, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedMeaning, setEditedMeaning] = useState(milestone.meaning || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    
    try {
      await updateMilestone(milestone.id, {
        meaning: editedMeaning
      });
      
      // Notify parent of update
      if (onUpdate) {
        onUpdate({ ...milestone, meaning: editedMeaning });
      }
      
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to update milestone:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedMeaning(milestone.meaning || '');
    setIsEditing(false);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-100 p-5 transition-colors duration-150 hover:border-gray-200">
      {/* Header: Titel + Global Period */}
      <div className="flex items-start justify-between gap-4">
        {/* Title - clear but not bold+color together */}
        <h4 className="text-sm font-medium text-gray-800">
          {milestone.title}
        </h4>
        {/* Global period - very subtle context */}
        {milestone.globalPeriod && (
          <span className="text-[11px] text-gray-300 whitespace-nowrap">
            {milestone.globalPeriod}
          </span>
        )}
      </div>

      {/* Sparring Question - if available, show the question that led to this answer */}
      {milestone.sparringQuestion && (
        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
          <p className="text-[11px] text-blue-600 font-medium mb-1 flex items-center gap-1"><ChatIcon size={14} className="text-blue-600" /> Sparring vraag:</p>
          <p className="text-sm text-blue-800 leading-relaxed">
            {milestone.sparringQuestion}
          </p>
        </div>
      )}

      {/* Meaning/Answer - editable */}
      <div className="mt-3">
        {milestone.meaning && (
          <p className="text-[11px] text-gray-400 mb-1">
            <span className="flex items-center gap-1"><DocumentIcon size={14} className="text-gray-400" /> {milestone.sparringQuestion ? 'Jouw antwoord:' : 'Betekenis:'}</span>
          </p>
        )}
        
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              value={editedMeaning}
              onChange={(e) => setEditedMeaning(e.target.value)}
              className="w-full px-3 py-2 text-sm text-gray-700 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
              rows={4}
              placeholder="Beschrijf wat dit voor jou betekent..."
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={handleCancel}
                className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 transition-colors"
                disabled={isSaving}
              >
                Annuleren
              </button>
              <button
                onClick={handleSave}
                className="px-3 py-1.5 text-xs bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
                disabled={isSaving}
              >
                {isSaving ? 'Opslaan...' : 'Opslaan'}
              </button>
            </div>
          </div>
        ) : (
          <div className="group">
            {milestone.meaning ? (
              <p className="text-sm text-gray-600 leading-relaxed">
                {milestone.meaning}
              </p>
            ) : (
              <p className="text-sm text-gray-400 italic">
                Nog geen betekenis toegevoegd
              </p>
            )}
            
            {/* Edit button - appears on hover */}
            <button
              onClick={() => setIsEditing(true)}
              className="mt-2 text-[11px] text-orange-500 hover:text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <EditIcon size={14} className="inline text-orange-500" /> Wijzigen
            </button>
          </div>
        )}
      </div>

      {/* From Sparring indicator - very muted */}
      {milestone.fromSparring && (
        <p className="mt-4 text-[11px] text-gray-300">
          Ontstaan uit sparring
        </p>
      )}
    </div>
  );
};

export default MilestoneCard;
